import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  GET_SHARED_ALBUM_TYPEFORM,
} from '../../../../queries';
import {
  CAPTION_TYPEFORM_FIELD_ID,
  hashids,
  WELCOME_HOME_ALBUM_ID,
} from '../../../../utilities';
import hubTheme from '../../hubTheme';
import { useAlbumIcon } from '../useAlbumIcon';
import AlbumShareButton from '../AlbumShareButton';
import FloatingArtifacts from './FloatingArtifacts';
import SharedPostCard from './SharedPostCard';
import {
  AlbumSpecies,
  getAlbumSpecies,
  getWelcomeHomePromptsForSpecies,
  WELCOME_HOME_ALBUM_DESCRIPTION,
  WELCOME_HOME_ALBUM_TITLE,
} from '../welcomeHomeAlbum';

const PRICING_URL = 'https://petcademy.org/pricing/';

interface SharedPrompt {
  id: string;
  title: string;
  images?: string[];
  caption?: string;
}

interface SharedAlbumType {
  id: string;
  title: string;
  description: string;
  petName: string;
  petType: 'dog' | 'cat';
  photoCount: number;
  prompts: SharedPrompt[];
}

interface SharedAlbumTypeformData {
  sharedAlbumTypeform?: {
    animal?: {
      id: string;
      name: string;
      animalType: string;
    } | null;
    albumForm?: {
      id: string;
      questions: {
        id: string;
        title: string;
        hidden?: boolean | null;
      }[];
    } | null;
    responses: {
      id: string;
      submittedAt: string;
      answers: {
        id?: string | null;
        fieldId: string;
        fieldType: string;
        text?: string | null;
      }[];
    }[];
  } | null;
}

const extractImageUrls = (text?: string | null): string[] => {
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.filter((value): value is string => typeof value === 'string');
    }
    if (typeof parsed === 'string') {
      return [parsed];
    }
    if (parsed && typeof parsed === 'object' && 'url' in parsed && typeof parsed.url === 'string') {
      return [parsed.url];
    }
  } catch {
    // Typeform file uploads may be stored as a plain URL instead of JSON.
  }

  return text
    .split(/[\n,]/)
    .map((url) => url.trim())
    .filter((url) => url.startsWith('http'));
};

const buildSharedAlbum = (
  albumId: string,
  data?: SharedAlbumTypeformData['sharedAlbumTypeform'],
): SharedAlbumType | null => {
  if (!data?.animal || !data.albumForm) return null;

  const species: AlbumSpecies = getAlbumSpecies(data.animal);
  const promptTemplates = getWelcomeHomePromptsForSpecies(species);
  const visibleQuestions = data.albumForm.questions
    .filter((question) => !question.hidden && question.id !== CAPTION_TYPEFORM_FIELD_ID)
    .slice(0, promptTemplates.length);
  const completedAnswersByFieldId = new Map<
    string,
    { caption?: string; images: string[] }
  >();

  data.responses.forEach((response) => {
    const caption = response.answers.find(
      (answer) => answer.fieldId === CAPTION_TYPEFORM_FIELD_ID,
    )?.text ?? undefined;

    response.answers.forEach((answer) => {
      if (!answer.id || answer.fieldType !== 'file_upload') return;
      const existingAnswer = completedAnswersByFieldId.get(answer.fieldId);
      completedAnswersByFieldId.set(answer.fieldId, {
        images: [
          ...(existingAnswer?.images ?? []),
          ...extractImageUrls(answer.text),
        ],
        caption: caption ?? existingAnswer?.caption,
      });
    });
  });

  const prompts = visibleQuestions.map((question, index) => {
    const completedAnswer = completedAnswersByFieldId.get(question.id);
    const promptTemplate = promptTemplates[index];

    return {
      id: question.id,
      title: promptTemplate?.title || question.title,
      images: completedAnswer?.images,
      caption: completedAnswer?.caption,
    };
  }).filter(
    (prompt) => prompt.images && prompt.images.length > 0,
  );
  const photoCount = prompts.reduce((total, prompt) => total + (prompt.images?.length ?? 0), 0);

  return {
    id: albumId,
    title: WELCOME_HOME_ALBUM_TITLE,
    description: WELCOME_HOME_ALBUM_DESCRIPTION,
    petName: data.animal.name,
    petType: species,
    photoCount,
    prompts,
  };
};

function CreatedByCallout() {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      component="a"
      href={PRICING_URL}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        textDecoration: 'none',
        color: 'primary.dark',
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Lexend, sans-serif',
          fontWeight: 500,
          fontSize: '14px',
          lineHeight: 1.25,
          color: '#23036a',
          whiteSpace: 'nowrap',
        }}
      >
        Created by
      </Typography>
      <Box
        component="img"
        src="/logo_hub.svg"
        alt="Petcademy"
        sx={{ height: 19, width: 'auto', display: 'block' }}
      />
    </Stack>
  );
}

interface ShareStoryButtonProps {
  fullWidth?: boolean;
  useNativeShare?: boolean;
  onCopySuccess: () => void;
}

function ShareStoryButton({
  fullWidth, useNativeShare, onCopySuccess,
}: ShareStoryButtonProps) {
  return (
    <AlbumShareButton
      label="Share Story"
      url={typeof window !== 'undefined' ? window.location.href : ''}
      useNativeShare={useNativeShare}
      onCopySuccess={onCopySuccess}
      buttonSx={{
        whiteSpace: 'nowrap',
        flexShrink: 0,
        width: fullWidth ? '100%' : '240px',
        ...(fullWidth && { height: 58 }),
      }}
    />
  );
}

// Updates document.title + Open Graph / Twitter meta tags so that link previews
// reflect the album's pet name and cover photo.
//
// NOTE: Most social crawlers (Facebook's especially) do NOT execute JavaScript.
// For the share preview to work in production, a Netlify Edge Function should
// inject these meta tags server-side based on the URL params. This client-side
// update keeps the browser tab title correct and is enough for crawlers that do
// run JS (e.g. Twitter's preview generator does).
function useShareMeta(opts: {
  title: string;
  description: string;
  image: string;
  url: string;
}) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = opts.title;

    type Tag =
      | { property: string; content: string }
      | { name: string; content: string };
    const tags: Tag[] = [
      { property: 'og:title', content: opts.title },
      { property: 'og:description', content: opts.description },
      { property: 'og:image', content: opts.image },
      { property: 'og:url', content: opts.url },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: opts.title },
      { name: 'twitter:description', content: opts.description },
      { name: 'twitter:image', content: opts.image },
      { name: 'description', content: opts.description },
    ];

    const created: HTMLMetaElement[] = [];
    tags.forEach((tag) => {
      const selector = 'property' in tag
          ? `meta[property="${tag.property}"]`
          : `meta[name="${tag.name}"]`;
      let el = document.head.querySelector<HTMLMetaElement>(selector);
      if (!el) {
        el = document.createElement('meta');
        if ('property' in tag) el.setAttribute('property', tag.property);
        else el.setAttribute('name', tag.name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', tag.content);
    });

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [opts.title, opts.description, opts.image, opts.url]);
}

function getCoverImage(): string {
  if (typeof window === 'undefined') return '';
  return `https://${window.location.host}/hub/share_bg.jpg`;
}

interface CoverProps {
  title: string;
  subtitle: string;
  photoCount: number;
  icon: React.ReactNode;
}

function Cover({
 title, subtitle, photoCount, icon,
}: CoverProps) {
  return (
    <Box sx={{ pt: 1.5, px: 1.5 }}>
      <Box
        sx={{
          backgroundColor: '#000',
          backgroundImage: 'url(/hub/share_bg.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderRadius: '20px',
          color: '#fff',
          textAlign: 'center',
          px: { xs: 3, md: 6 },
          pt: { xs: 12, md: 7.5 },
          pb: { xs: 12, md: 14 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
          }}
        />
        <Stack spacing={1.5} alignItems="center" sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              '& svg': { width: 50, height: 50 },
            }}
          >
            {icon}
          </Box>
          <Typography
            sx={{
              fontFamily: 'Lexend, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '28px', md: '36px' },
              lineHeight: 1.2,
              letterSpacing: '-0.5px',
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lexend, sans-serif',
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: 1.25,
              maxWidth: 560,
            }}
          >
            {subtitle}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lexend, sans-serif',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: 1.25,
              pt: 1,
            }}
          >
            {`${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
}

function JoinCallout() {
  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        borderRadius: '18px',
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.08)',
        textAlign: 'center',
        px: { xs: 3, md: 5.5 },
        py: 5,
      }}
    >
      <Stack spacing={2.5} alignItems="center">
        <Box
          component="img"
          src="/pet_family.png"
          alt="Petcademy"
          sx={{ width: 143, height: 143, objectFit: 'contain' }}
        />
        <Stack spacing={0.75} alignItems="center">
          <Typography
            sx={{
              fontFamily: 'Rasa, "Lexend", sans-serif',
              fontWeight: 500,
              fontSize: '24px',
              lineHeight: 1,
              letterSpacing: '-0.15px',
            }}
          >
            Want to create a story like this?
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Lexend, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: 1.4,
              color: 'text.primary',
            }}
          >
            Capture your pet&apos;s moments and turn them into a shareable
            album.
          </Typography>
        </Stack>
        <Button
          variant="contained"
          href={PRICING_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ width: 260 }}
        >
          Join Petcademy
        </Button>
      </Stack>
    </Box>
  );
}

function SharedAlbumContent() {
  const { albumId, petId } = useParams<{ albumId: string; petId: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [copyConfirmOpen, setCopyConfirmOpen] = useState(false);
  const decodedPetId = petId ? hashids.decode(petId)[0]?.toString() : null;

  const { data, loading, error } = useQuery<
    SharedAlbumTypeformData,
    { typeformFormId: string; animalId: string }
  >(GET_SHARED_ALBUM_TYPEFORM, {
    variables: {
      typeformFormId: WELCOME_HOME_ALBUM_ID || '',
      animalId: decodedPetId || '',
    },
    skip: !albumId || !decodedPetId || !WELCOME_HOME_ALBUM_ID,
  });
  const album = buildSharedAlbum(albumId || '', data?.sharedAlbumTypeform);

  const { icon } = useAlbumIcon('welcome-home');

  const petName = album?.petName || '';
  const fillPetName = (text: string) => text
      .replace(/\[pet name\]/g, petName)
      .replace(/Your Pet/g, petName)
      .replace(/your pet/g, petName);

  const albumTitle = album ? `${fillPetName(album.title)}, ${petName}!` : '';
  const albumSubtitle = album ? fillPetName(album.description) : '';
  const shareTitle = album ? `Welcome Home ${petName} 🐾` : '';
  const shareDescription = album
    ? `See the special moments from ${petName}'s journey home.`
    : '';
  const shareImage = album ? getCoverImage() : '';

  useShareMeta({
    title: shareTitle,
    description: shareDescription,
    image: shareImage,
    url: typeof window !== 'undefined' ? window.location.href : '',
  });

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (error || !album) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Album not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F6F5FA' }}>
      <Box
        component="header"
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: { xs: 2, md: 4, lg: 25 },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={{ xs: 'center', md: 'space-between' }}
          sx={{ height: 76, py: 1.5 }}
        >
          <CreatedByCallout />
          {!isMobile && (
            <ShareStoryButton onCopySuccess={() => setCopyConfirmOpen(true)} />
          )}
        </Stack>
      </Box>

      <Box sx={{ pb: { xs: 6, md: 10 }, position: 'relative' }}>
        <Cover
          title={albumTitle}
          subtitle={albumSubtitle}
          photoCount={album.photoCount}
          icon={icon}
        />

        {isMobile && (
          <Box
            sx={{
 display: 'flex', justifyContent: 'center', mt: -4, px: 8,
}}
          >
            <Box
              sx={{
                p: 4,
                width: '100%',
                maxWidth: '85vw',
                position: 'relative',
                zIndex: 1,
                backgroundColor: '#F6F5FA',
                borderRadius: '8px',
              }}
            >
              <ShareStoryButton
                fullWidth
                useNativeShare
                onCopySuccess={() => setCopyConfirmOpen(true)}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ position: 'relative' }}>
          <FloatingArtifacts />

          <Box
            sx={{
              mt: { xs: 2, md: -10 },
              position: 'relative',
              zIndex: 1,
              px: { xs: 2, md: 6, lg: 40 },
            }}
          >
            <Stack spacing={5}>
              {album.prompts.map((prompt: SharedPrompt) => (
                <SharedPostCard
                  key={prompt.id}
                  title={fillPetName(prompt.title)}
                  images={prompt.images || []}
                  caption={prompt.caption ? fillPetName(prompt.caption) : ''}
                />
              ))}
            </Stack>
          </Box>

          <Box
            sx={{
              px: { xs: 2, md: 6, lg: 40 },
              pt: { xs: 5, md: 5 },
              position: 'relative',
              zIndex: 1,
            }}
          >
            <JoinCallout />
          </Box>
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          py: 6,
        }}
      >
        <Box
          component="img"
          src="/logo_hub.svg"
          alt="Petcademy"
          sx={{ opacity: 0.55, filter: 'grayscale(100%)', height: 19 }}
        />
      </Box>

      <Snackbar
        open={copyConfirmOpen}
        autoHideDuration={3000}
        onClose={() => setCopyConfirmOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message="Link copied"
      />
    </Box>
  );
}

function SharedAlbum() {
  return (
    <ThemeProvider theme={hubTheme}>
      <SharedAlbumContent />
    </ThemeProvider>
  );
}

export default SharedAlbum;
